/**
 * @class
 * @function CustomSlider
 */
if (!customElements.get('custom-slider')) {
  class CustomSlider extends HTMLElement {
    constructor() {
      super();
      this.currentSlide = 0;
      this.totalSlides = 0;
      this.autoplayTimer = null;
      this.isAnimating = false;
      this.touchStartX = 0;
      this.touchEndX = 0;
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.clearAutoplay();
      this.removeEventListeners();
    }

    init() {
      this.slider = this.querySelector('.custom-slider--inner');
      this.slides = Array.from(this.querySelectorAll('.custom-slider--slide'));
      this.themeScroll = this.querySelector('theme-scroll');
      this.scrollContainer = this.querySelector('.theme-scroll--inner');
      this.prevButton = this.querySelector('.flickity-prev');
      this.nextButton = this.querySelector('.flickity-next');
      
      this.totalSlides = this.slides.length;
      
      if (this.totalSlides === 0) return;

      // Get settings from data attributes
      this.autoplay = this.dataset.autoplay !== 'false' ? parseInt(this.dataset.autoplay, 10) : false;
      this.showArrows = this.dataset.showArrows === 'true';
      this.slideSpacing = this.dataset.slideSpacing || 'medium';

      this.setupSlider();
      this.bindEvents();
      
      if (this.autoplay) {
        this.startAutoplay();
      }

      // Handle resize events
      window.addEventListener('resize', this.handleResize.bind(this));
    }

    setupSlider() {
      // Calculate slider width based on slides and spacing
      this.calculateSliderDimensions();
      
      // The theme-scroll component handles the scrolling behavior
      // No need to set transform positions manually
    }

    calculateSliderDimensions() {
      if (this.slides.length === 0) return;

      // Get gap value from CSS custom property
      const computedStyle = getComputedStyle(this.slider);
      const gap = parseInt(computedStyle.getPropertyValue('--gap')) || 20;
      
      // Calculate total width needed
      let totalWidth = 0;
      this.slides.forEach((slide, index) => {
        const slideWidth = parseInt(slide.style.getPropertyValue('--slide-width')) || 400;
        totalWidth += slideWidth;
        if (index < this.slides.length - 1) {
          totalWidth += gap;
        }
      });

      // Set slider width
      this.slider.style.width = `${totalWidth}px`;
    }

    bindEvents() {
      // Arrow navigation - now using theme-scroll navigation
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.scrollToPrevious());
      }
      
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.scrollToNext());
      }

      // Scroll event listener for button state management
      if (this.scrollContainer) {
        this.debouncedOnScroll = this.debounce(() => {
          this.updateNavigationState();
        }, 100);
        
        this.scrollContainer.addEventListener('scroll', this.debouncedOnScroll);
        this.scrollContainer.addEventListener('resize', this.debouncedOnScroll);
      }

      // Keyboard navigation
      this.addEventListener('keydown', this.handleKeydown.bind(this));

      // Pause autoplay on hover
      this.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.addEventListener('mouseleave', () => this.resumeAutoplay());

      // Pause autoplay when tab is not visible
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.pauseAutoplay();
        } else {
          this.resumeAutoplay();
        }
      });

      // Initial navigation state
      setTimeout(() => this.updateNavigationState(), 100);
    }

    addTouchSupport() {
      this.slider.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
      this.slider.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });
    }

    handleTouchStart(e) {
      this.touchStartX = e.changedTouches[0].screenX;
    }

    handleTouchEnd(e) {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    }

    handleSwipe() {
      const swipeThreshold = 50;
      const diff = this.touchStartX - this.touchEndX;

      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          // Swipe left - next slide
          this.scrollToNext();
        } else {
          // Swipe right - previous slide
          this.scrollToPrevious();
        }
      }
    }

    handleKeydown(e) {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.scrollToPrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.scrollToNext();
          break;
        case 'Home':
          e.preventDefault();
          if (this.scrollContainer) {
            this.scrollContainer.scrollTo({ left: 0, behavior: 'smooth' });
          }
          break;
        case 'End':
          e.preventDefault();
          if (this.scrollContainer) {
            const maxScroll = this.scrollContainer.scrollWidth - this.scrollContainer.clientWidth;
            this.scrollContainer.scrollTo({ left: maxScroll, behavior: 'smooth' });
          }
          break;
      }
    }

    handleResize() {
      // Recalculate dimensions on resize
      this.calculateSliderDimensions();
      this.updateSlider();
    }

    scrollToPrevious() {
      if (!this.scrollContainer) return;
      
      const slideWidth = this.getAverageSlideWidth();
      const currentScroll = this.scrollContainer.scrollLeft;
      const newScroll = Math.max(0, currentScroll - slideWidth);
      
      this.scrollContainer.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
      
      // Update navigation state after scroll
      setTimeout(() => this.updateNavigationState(), 300);
    }

    scrollToNext() {
      if (!this.scrollContainer) return;
      
      const slideWidth = this.getAverageSlideWidth();
      const currentScroll = this.scrollContainer.scrollLeft;
      const maxScroll = this.scrollContainer.scrollWidth - this.scrollContainer.clientWidth;
      const newScroll = Math.min(maxScroll, currentScroll + slideWidth);
      
      this.scrollContainer.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
      
      // Update navigation state after scroll
      setTimeout(() => this.updateNavigationState(), 300);
    }

    getAverageSlideWidth() {
      if (this.slides.length === 0) return 300;
      
      // Calculate average slide width for scrolling
      let totalWidth = 0;
      this.slides.forEach(slide => {
        const slideWidth = parseInt(slide.style.getPropertyValue('--slide-width')) || 300;
        totalWidth += slideWidth;
      });
      
      return totalWidth / this.slides.length;
    }

    updateSlider() {
      // The theme-scroll component handles the visual updates
      // We just need to ensure dimensions are correct
      this.calculateSliderDimensions();
      
      // Update navigation state after dimensions change
      setTimeout(() => this.updateNavigationState(), 100);
    }

    updateNavigationState() {
      if (!this.scrollContainer || !this.prevButton || !this.nextButton) return;

      // Calculate if we've reached the end (same logic as theme-scroll)
      const reachedEnd = this.scrollContainer.scrollWidth - this.scrollContainer.offsetWidth <= Math.floor(this.scrollContainer.scrollLeft);
      
      // Update button states (same logic as theme-scroll)
      this.prevButton.disabled = this.scrollContainer.scrollLeft === 0;
      this.nextButton.disabled = reachedEnd;
    }

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    startAutoplay() {
      if (!this.autoplay || this.totalSlides <= 1) return;
      
      this.autoplayTimer = setInterval(() => {
        this.scrollToNext();
      }, this.autoplay);
    }

    pauseAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    resumeAutoplay() {
      if (this.autoplay && !this.autoplayTimer) {
        this.startAutoplay();
      }
    }

    clearAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    removeEventListeners() {
      window.removeEventListener('resize', this.handleResize.bind(this));
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    // Public API methods
    getTotalSlides() {
      return this.totalSlides;
    }

    play() {
      this.startAutoplay();
    }

    pause() {
      this.pauseAutoplay();
    }

    destroy() {
      this.clearAutoplay();
      this.removeEventListeners();
    }
  }

  customElements.define('custom-slider', CustomSlider);
}

// Initialize sliders when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const sliders = document.querySelectorAll('custom-slider');
  sliders.forEach(slider => {
    // Slider will auto-initialize via connectedCallback
  });
});

// Handle theme editor events
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const slider = event.target.querySelector('custom-slider');
    if (slider) {
      // Re-initialize slider after section reload
      slider.init();
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const slider = event.target.querySelector('custom-slider');
    if (slider) {
      slider.destroy();
    }
  });

  document.addEventListener('shopify:block:select', (event) => {
    const slider = event.target.closest('custom-slider');
    if (slider) {
      slider.pause(); // Pause autoplay when block is selected in theme editor
    }
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const slider = event.target.closest('custom-slider');
    if (slider && slider.autoplay) {
      slider.play(); // Resume autoplay when block is deselected
    }
  });
}
